import { GET_SLASH_DOMAIN } from '@lib/config-client';
import { BurstCache } from '@lib/helpers/burst-cache';
import { toErrorWithMessage } from '@tuic/lib/helpers/error';

import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type {
  ClientBookingListResponse,
  GetSlashClientLoginResponse,
  GetSlashClientLogoutResponse,
  GetSlashClientSessionResponse,
} from '@modules/get-slash-internet-card';

type BaseResponse = {
  success: boolean;
  error?: {
    name: string;
    message: string;
  };
};

type GetSlashBooking = {
  id: number;
  active: boolean;
  tariffName: string;
  remainingTime: number;
  remainingVolume: number;
  maxTime: number | undefined;
  maxVolume: number | undefined;
  tariffId: number;
  usedTime: number;
  usedVolume: number;
  maxClients?: number;
};

export type GetSlashTariff = {
  id: number;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
};

export type BookingAddResponse = BaseResponse & {
  success: boolean;
  id?: number;
};

export type BookingListResponse = BaseResponse & {
  bookings: GetSlashBooking[];
};

export type LoginResponse = BaseResponse & {
  success: boolean;
};

export type LogoutResponse = BaseResponse & {
  success: boolean;
};

export type StatusResponse = BaseResponse & {
  success: boolean;
  cloud: boolean;
  active: boolean;
  bookingId?: number;
  tariffName?: string;
  remainingVolume?: number;
  maxVolume?: number;
  usedVolume?: number;
  remainingTime?: number;
  usedTime?: number;
};

export type TariffListResponse = BaseResponse & {
  success: boolean;
  tariffs: GetSlashTariff[];
};

// Module-level caches for deduplicating requests
const statusRequestCache = new BurstCache<StatusResponse>(1000); // Cache for 1 second
const bookingListRequestCache = new BurstCache<BookingListResponse>(1000); // Cache for 1 second

/**
 * Client for interacting with the Get Slash API for internet services on board.
 * Provides methods for booking tariffs, managing sessions, and retrieving status information.
 */
export default class GetSlash {
  private readonly credentials?: GetSlashCredentials;
  private readonly useDebugEndpoints: boolean;

  private readonly endpointBookingAdd: string;
  private readonly endpointBookingList: string;
  private readonly endpointLogin: string;
  private readonly endpointLogout: string;
  private readonly endpointStatus: string;
  private readonly endpointTariffList: string;

  private readonly requestConfig: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    mode: 'cors',
  };

  /**
   * Creates a new GetSlash client instance.
   *
   * @param args - Configuration options
   * @param args.credentials - Optional authentication credentials for the Get Slash API (required for booking/login operations)
   * @param args.useDebugEndpoints - When true, uses mock endpoints instead of the real API
   */
  constructor(args: {
    credentials?: GetSlashCredentials;
    useDebugEndpoints?: boolean;
  }) {
    this.credentials = args.credentials;
    this.useDebugEndpoints = args.useDebugEndpoints || false;

    const domain = args.useDebugEndpoints
      ? '/api/get-slash-mock'
      : `${GET_SLASH_DOMAIN}/client`;

    this.endpointBookingAdd = `${domain}/clientBookingAdd`;
    this.endpointBookingList = `${domain}/clientBookingList`;
    this.endpointLogin = `${domain}/clientLogin`;
    this.endpointLogout = `${domain}/clientLogout`;
    this.endpointStatus = `${domain}/clientStatus`;
    this.endpointTariffList = `${domain}/clientTariffList`;
  }

  /**
   * Books a new internet tariff.
   *
   * @param args - Booking parameters
   * @param args.tariffId - ID of the tariff to book
   * @returns A promise that resolves to the booking response
   */
  public async bookingAdd(args: {
    tariffId: number;
  }): Promise<BookingAddResponse> {
    if (!this.credentials) {
      return {
        success: false,
        error: {
          name: 'MissingCredentialsError',
          message: 'Credentials are required for booking operations',
        },
      };
    }
    const requestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
      booking: {
        tariffId: args.tariffId,
      },
    })}`;

    try {
      const response = await fetch(this.endpointBookingAdd, {
        ...this.requestConfig,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.resultCode === 0 && data.result !== null) {
        return {
          success: true,
          id: data.result.id,
        };
      }

      return {
        success: false,
        error: {
          name: 'BookingAddError',
          message:
            data.resultMsg ??
            data.resultAttachment?.messageString ??
            'Failed to book tariff',
        },
      };
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        error: {
          name: err.name,
          message: err.message,
        },
      };
    } finally {
      // Always clear caches after booking attempt
      statusRequestCache.clear();
      bookingListRequestCache.clear();
    }
  }

  /**
   * Retrieves a list of booked internet tariffs.
   * Implements request deduplication to prevent multiple parallel requests.
   *
   * @returns A promise that resolves to the booking list response
   */
  public async bookingList(): Promise<BookingListResponse> {
    // Use burst cache to deduplicate concurrent requests
    return bookingListRequestCache.get(() => this.performBookingListRequest());
  }

  /**
   * Performs the actual booking list request without caching.
   * This is separated to keep the caching logic clean.
   */
  private async performBookingListRequest(): Promise<BookingListResponse> {
    if (!this.credentials) {
      return {
        success: false,
        error: {
          name: 'MissingCredentialsError',
          message: 'Credentials are required for booking list operations',
        },
        bookings: [],
      };
    }
    const requestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
      children: { '*': 1 },
      filter: 'all',
    })}`;

    let data: ClientBookingListResponse;

    try {
      const response = await fetch(this.endpointBookingList, {
        ...this.requestConfig,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      data = (await response.json()) as ClientBookingListResponse;
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        error: {
          name: err.name,
          message: err.message,
        },
        bookings: [],
      };
    }

    if (data.resultCode !== 0) {
      return {
        success: false,
        bookings: [],
        error: {
          name: 'GetSlash Error',
          message: data.resultMsg,
        },
      };
    }

    const bookings = (data.result ?? []).map((item): GetSlashBooking => {
      return {
        id: item.id,
        active: item.status === 'active',
        tariffName: item.tariff.name,
        remainingTime: item.remainingTime,
        remainingVolume: item.remainingVolume,
        maxTime: item.tariff.maxTime > 0 ? item.tariff.maxTime : undefined,
        maxVolume:
          item.tariff.maxVolume > 0 ? item.tariff.maxVolume : undefined,
        tariffId: item.tariffId,
        usedTime: item.usedTime,
        usedVolume: item.usedVolume,
        maxClients: item.tariff.maxClients,
      };
    });

    return {
      success: true,
      bookings,
    };
  }

  public async validateCredentials(): Promise<boolean> {
    const resp = await this.bookingList();

    if (resp.success) {
      return true;
    }

    const message = resp.error?.message.toLowerCase();

    if (!message) {
      return true;
    }

    return !message.includes('invalid credentials');
  }

  /**
   * Starts an internet session for a booked tariff.
   *
   * @param args - Login parameters
   * @param args.bookingId - ID of the booking to activate
   * @returns A promise that resolves to the login response
   */
  public async login(args: { bookingId: number }): Promise<LoginResponse> {
    if (!this.credentials) {
      return {
        success: false,
        error: {
          name: 'MissingCredentialsError',
          message: 'Credentials are required for login operations',
        },
      };
    }
    const requestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
      ...args,
    })}`;

    let data: GetSlashClientLoginResponse;

    try {
      const response = await fetch(this.endpointLogin, {
        ...this.requestConfig,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      data = (await response.json()) as GetSlashClientLoginResponse;
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        error: {
          name: err.name,
          message: err.message,
        },
      };
    } finally {
      // Always clear caches after login attempt
      statusRequestCache.clear();
      bookingListRequestCache.clear();
    }

    if (data.resultCode === 0 && data.result !== null) {
      return {
        success: true,
      };
    }

    return {
      success: false,
      error: {
        name: 'LoginError',
        message:
          data.resultMsg ??
          data.resultAttachment?.messageString ??
          'Failed to start internet session',
      },
    };
  }

  /**
   * Ends the current internet session.
   *
   * @returns A promise that resolves to the logout response
   */
  public async logout(): Promise<LogoutResponse> {
    let data: GetSlashClientLogoutResponse;

    try {
      const response = await fetch(this.endpointLogout, this.requestConfig);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      data = (await response.json()) as GetSlashClientLogoutResponse;
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        error: {
          name: err.name,
          message: err.message,
        },
      };
    } finally {
      // Always clear caches after logout attempt
      statusRequestCache.clear();
      bookingListRequestCache.clear();
    }

    // Check if logout was successful
    if (data.resultCode === 0 || data.result !== null) {
      return {
        success: true,
      };
    }

    return {
      success: false,
      error: {
        name: 'LogoutError',
        message:
          data.resultMsg ??
          data.resultAttachment?.messageString ??
          'Failed to end internet session',
      },
    };
  }

  /**
   * Retrieves the current internet session status.
   * Implements request deduplication to prevent multiple parallel requests.
   *
   * @returns A promise that resolves to the status response
   */
  public async status(): Promise<StatusResponse> {
    // Use burst cache to deduplicate concurrent requests
    return statusRequestCache.get(() => this.performStatusRequest());
  }

  /**
   * Performs the actual status request without caching.
   * This is separated to keep the caching logic clean.
   */
  private async performStatusRequest(): Promise<StatusResponse> {
    let sessionData: GetSlashClientSessionResponse;

    try {
      const response = await fetch(this.endpointStatus, this.requestConfig);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      sessionData = (await response.json()) as GetSlashClientSessionResponse;
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        cloud: true, // Failed request = not on ship
        active: false,
        error: {
          name: err.name,
          message: err.message,
        },
      };
    }

    const sessionResult = sessionData.result;
    // Determine cloud status: true = cloud, false = ship
    const cloud = 'cloud' in sessionData && (sessionData as any).cloud === true;

    if (sessionData.resultCode !== 0 || sessionResult === null) {
      return {
        success: true,
        cloud,
        active: false,
      };
    }

    const maxVolume =
      sessionResult.bookedTariffMaxVolume > 0
        ? sessionResult.bookedTariffMaxVolume
        : undefined;

    const remainingVolume =
      sessionResult.remainingVolume >= 0
        ? sessionResult.remainingVolume
        : undefined;

    const usedVolume =
      typeof maxVolume !== 'undefined' && typeof remainingVolume !== 'undefined'
        ? maxVolume - remainingVolume
        : undefined;

    const remainingTime =
      sessionResult.remainingTime >= 0
        ? sessionResult.remainingTime
        : undefined;

    return {
      success: true,
      cloud,
      active: true,
      tariffName: sessionResult.bookedTariff,
      bookingId: sessionResult.bookingId,
      maxVolume,
      remainingVolume,
      usedVolume,
      remainingTime,
    };
  }

  /**
   * Retrieves a list of available internet tariffs.
   *
   * @returns A promise that resolves to the tariff list response
   */
  public async tariffList(): Promise<TariffListResponse> {
    if (!this.credentials) {
      return {
        success: false,
        error: {
          name: 'MissingCredentialsError',
          message: 'Credentials are required for tariff list operations',
        },
        tariffs: [],
      };
    }
    const requestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
    })}`;

    try {
      const response = await fetch(this.endpointTariffList, {
        ...this.requestConfig,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.resultCode === 0 && data.result !== null) {
        // Transform the API response into our GetSlashTariff type
        const tariffs = Array.isArray(data.result)
          ? data.result.map((item: any): GetSlashTariff => {
              return {
                id: item.id,
                name: item.name,
                description: item.description,
                price: Math.max(item.volumePrice ?? 0, item.timePrice ?? 0),
                currency: 'EUR',
              };
            })
          : [];

        return {
          success: true,
          tariffs,
        };
      }

      return {
        success: false,
        tariffs: [],
        error: {
          name: 'TariffListError',
          message:
            data.resultMsg ??
            data.resultAttachment?.messageString ??
            'Failed to fetch tariffs',
        },
      };
    } catch (e) {
      const err = toErrorWithMessage(e);

      return {
        success: false,
        tariffs: [],
        error: {
          name: err.name,
          message: err.message,
        },
      };
    }
  }

  /**
   * Retrieves debug data by making requests to multiple endpoints.
   *
   * @returns A promise that resolves to an object containing debug data
   */
  public async getDebugData(): Promise<{
    startedAt: string;
    endedAt: string;
    useDebugEndpoints: boolean;
    credentials?: GetSlashCredentials;
    clientBookingList: any;
    clientTariffList: any;
    clientStatus: any;
    clientBookingListError?: { name: string; message: string };
    clientTariffListError?: { name: string; message: string };
    clientStatusError?: { name: string; message: string };
  }> {
    const startedAt = new Date().toISOString();

    const tryRequest = async (
      requestFn: () => Promise<Response>,
    ): Promise<any> => {
      try {
        const response = await requestFn();
        return await response.json();
      } catch (e) {
        const err = toErrorWithMessage(e);
        return {
          error: {
            name: err.name,
            message: err.message,
          },
        };
      }
    };

    // Prepare request bodies
    const bookingListRequestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
      children: { '*': 1 },
      filter: 'all',
    })}`;

    const tariffListRequestBody = `jsondata=${JSON.stringify({
      ...this.credentials,
    })}`;

    // Execute all requests in parallel using the helper function
    const [bookingListResult, statusResult, tariffListResult] =
      await Promise.all([
        tryRequest(() =>
          fetch(this.endpointBookingList, {
            ...this.requestConfig,
            body: bookingListRequestBody,
          }),
        ),
        tryRequest(() => fetch(this.endpointStatus, this.requestConfig)),
        tryRequest(() =>
          fetch(this.endpointTariffList, {
            ...this.requestConfig,
            body: tariffListRequestBody,
          }),
        ),
      ]);

    return {
      startedAt,
      endedAt: new Date().toISOString(),
      useDebugEndpoints: this.useDebugEndpoints,
      credentials: this.credentials,
      clientBookingList: bookingListResult,
      clientStatus: statusResult,
      clientTariffList: tariffListResult,
    };
  }
}
